// Importing Components
import { FormElems, ConditionalComponent, Alert, DatePicker, FakeLoader } from 'components';

// Importing Constants
import { CHARGEBACK_STATUS_ENUM, MAX_25MB, MAX_5MB } from '../../utils/constants/commonValues';

// Importing Actions
import {
    validateChargeBackRequest,
    raiseChargeBackRequest,
    resetRaiseChargebackForm,
} from '../../containers/Chargeback/redux/chargeback.actions';
import { CHARGEBACK_SEARCH_OPTIONS } from './constant';

// Importing Validators
import { raiseChargebackFormValidator } from './validators';

// Importing Libraries
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, change } from 'redux-form';
import { connect } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import moment from 'moment';

import './raiseChargebackForm.scss';

const RaiseChargebackForm = ({
    handleSubmit,
    formData,
    onSuccess,
    onCancel,
    actions,
    chargebackStoreData,
    staticData,
    initialValues,
    transactionData,
}) => {
    const { raisedChargebackData } = chargebackStoreData;
    const { chargebackStaticInfo } = staticData;
    const { transactionModeListMap } = chargebackStaticInfo;
    let REASON_CODE_OPTIONS = [];
    let REASON_MESSAGE_OPTIONS = [];

    const [chargebackReportDate, setChargebackReportDate] = useState(
        moment().endOf('day')
    );

    const [focusedInput, setFocusedInput] = useState(null);
    const [fileList, setFileList] = useState([]);

    const getReasonCodeType = (paymentType, transactionSubMode) => {

        const NETWORK_MAPPING = {
            VI: "VISA",
            MC: "MASTERCARD",
            RPAY: "RUPAY",
            AMEX: "AMEX",
            DINR: "DINERS",
            DISCOVER: "DISCOVER"
        }

        const subMode = NETWORK_MAPPING[transactionSubMode] ? NETWORK_MAPPING[transactionSubMode] : transactionSubMode;

        if (paymentType && ["UPI"].includes(paymentType)) {
            return paymentType;
        }

        if (subMode && transactionModeListMap[subMode] && transactionModeListMap[subMode].length > 0) {
            return subMode;
        }

        return 'OTHER'
    }

    const PaymentType = getReasonCodeType(initialValues?.paymentType, initialValues?.transactionSubMode)

    if (transactionModeListMap && transactionModeListMap[PaymentType]) {
        REASON_CODE_OPTIONS = transactionModeListMap[PaymentType].map(
            (item, index) => {
                return {
                    label: item?.reasonCode,
                    value: item?.reasonCode,
                };
            }
        );


        REASON_MESSAGE_OPTIONS = transactionModeListMap[PaymentType].map(
            (item, index) => {
                return {
                    label: item?.reasonMessage,
                    value: item?.reasonMessage,
                };
            }
        );
    }

    useEffect(() => {
        if (
            formData?.values?.reasonCode &&
            transactionModeListMap[PaymentType]
        ) {
            const selectedReason = transactionModeListMap[PaymentType].find(
                item => item.reasonCode === formData?.values?.reasonCode
            );

            const reasonMessage = selectedReason?.reasonMessage;

            actions.change(
                'raise-chargeback-form',
                'reasonCodeMsg',
                reasonMessage
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.values?.reasonCode]);

    useEffect(() => {
        if (raisedChargebackData?.isChargebackRaisedSuccess) {
            actions.resetRaiseChargebackForm()
            onSuccess();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raisedChargebackData?.isChargebackRaisedSuccess]);

    const handleRaiseChargebackFormSubmit = values => {
        actions.raiseChargeBackRequest(values);
    };

    const handleRaiseChargebackCancel = () => {
        actions.resetRaiseChargebackForm()
        if (onCancel && typeof onCancel === "function") {
            onCancel(actions.resetRaiseChargebackForm);
        }
    }

    return (
        <form
            className="raise-chargeback-form"
            onSubmit={handleSubmit(handleRaiseChargebackFormSubmit)}
        >
            {raisedChargebackData?.isLoading ? <FakeLoader withMask /> : <></>}
            <div className="form-section">
                <div className="form-field">
                    <Field
                        name="reportedDate"
                        component={DatePicker}
                        singleDatePicker
                        startDateId="reportedDate"
                        onFocusedInput={setFocusedInput}
                        onSetStartDate={setChargebackReportDate}
                        startDate={chargebackReportDate}
                        focusedInput={focusedInput}
                        isOutsideRange={day => {
                            return moment().diff(day.endOf('day'), 'days') < 0;
                        }}
                    />
                </div>
                <div className="form-field">
                    <Field
                        name="rrnNumber"
                        type="text"
                        id="rrnNumber"
                        labelText="RRN No."
                        component={FormElems.LabelInput}
                    />
                </div>
            </div>

            <div className="form-section">
                <div className="form-field">
                    <Field
                        name="reasonCode"
                        id="reasonCode"
                        label="Select/Search Reason Code"
                        component={FormElems.SearchSelect}
                        labelField="label"
                        valueField="value"
                        data={REASON_CODE_OPTIONS || []}
                    />
                </div>
                <div className="form-field">
                    <Field
                        name="reasonCodeMsg"
                        id="reasonCodeMsg"
                        label="Select Message"
                        component={FormElems.SearchSelect}
                        disabled
                        isDisabled
                        labelField="label"
                        valueField="value"
                        data={REASON_MESSAGE_OPTIONS || []}
                    />
                </div>
            </div>
            <div className="form-section">
                <Field
                    name="files"
                    type="file"
                    onFileListChange={files => setFileList(files)}
                    fileList={fileList}
                    accept=".pdf, .doc, .docx, .xls, .xlsx, .jpg, .jpeg, .png, .gif, .zip, .rar, .7zip"
                    subtitle="Maximum file size 25MB. Only PDF, doc, excel, .zip and image files are accepted"
                    component={FormElems.MultiUpload}
                    maxSize={MAX_25MB}
                />
            </div>

            <ConditionalComponent
                isVisible={
                    raisedChargebackData &&
                    raisedChargebackData?.error &&
                    raisedChargebackData?.error?.errorMessage
                }
            >
                <Alert customClass="error-msg">
                    {raisedChargebackData?.error?.errorMessage}
                </Alert>
            </ConditionalComponent>

            <div className="form-section-actions">
                <FormElems.Button
                    buttonType="secondary"
                    id="back-btn"
                    text="Cancel"
                    type="button"
                    handleClick={handleRaiseChargebackCancel}
                    customClass="back-btn"
                />
                <FormElems.Button
                    id="next-btn"
                    buttonType="primary"
                    text="Submit"
                />
            </div>
        </form>
    );
};

const mapStateToProps = ({ form, chargebackData, staticData }) => ({
    formData: form['raise-chargeback-form'],
    chargebackStoreData: chargebackData,
    staticData,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            validateChargeBackRequest,
            raiseChargeBackRequest,
            resetRaiseChargebackForm,
            change,
        },
        dispatch
    ),
});

export default compose(
    reduxForm({
        form: 'raise-chargeback-form',
        enableReinitialize: true,
        validate: raiseChargebackFormValidator,
    }),
    connect(mapStateToProps, mapDispatchToProps)
)(RaiseChargebackForm);

// PropType validation for UpdateChargeBackStatusForm component
RaiseChargebackForm.propTypes = {
    formData: PropTypes.object,
    chargebackStoreData: PropTypes.object,
    staticData: PropTypes.object,
    actions: PropTypes.object,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func,
    handleSubmit: PropTypes.func,
    isTransactionPage: PropTypes.bool,
    transactionData: PropTypes.object,
};
